import { NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TooltipComponent } from 'src/app/domain/shared/component/tooltip/tooltip.component';
import { ToolTipPosition, ToolTipSettings } from 'src/app/domain/shared/component/tooltip/tooltip.util';
import { DEFAULT_TOOLTIP_DELAY_TIME, DelayService } from "../../core/service/delay.service";

@Component({
  selector: 'app-text-with-tooltip',
  templateUrl: './text-with-tooltip.component.html',
  styleUrl: './text-with-tooltip.component.scss',
  standalone: true,
  providers: [DelayService],
  imports: [NgIf, TooltipComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextWithTooltipComponent implements AfterViewInit {
  /**
   * The main text that will be displayed. It will be truncated if necessary,
   * and shown in a tooltip only when truncation occurs.
   */
  @Input() text = '';
  /**
   * Use this input to display custom text in the tooltip instead of the default {@link text}.
   */
  @Input() customTooltipText: string;
  /**
   * The ID of the div-element with truncated text
   */
  @Input() id: string;
  /**
   * The data-qa attribute
   * */
  @Input() dataQA: string;
  /**
   * The count of lines of {@link text} which will be displayed
   */
  @Input() linesCount: number = 1;
  /**
   *  Horizontal shift along the X-axis.
   */
  @Input() xShift = '0em';
  /**
   *  Vertical shift along the Y-axis.
   */
  @Input() yShift = '0em';

  /**
   * Defines the tooltip's position relative to the 3 dots element.
   *
   * @input positionType
   * @type {@link ToolTipPosition}
   * @default 'centered'
   *
   * Example usage:
   * ```html
   * <app-text-with-tooltip [positionType]="'left'"/>
   * ```
   */
  @Input() positionType: ToolTipPosition = 'centered';
  /**
   * Normally, the tooltip is activated only if the {@link text} is **truncated**.
   * However, if this flag is set to true, the tooltip will be shown even when the {@link text} is not truncated.
   *
   * For example, this is required in {@link GlobalCompanySearchComponent}:
   * the {@link text} contains a fund name, and we must always show a tooltip with the company name
   * when hovering over the {@link text}.
   */
  @Input() isTooltipAlwaysShownOnHover: boolean = false;

  @ViewChild('tooltip') tooltip: ElementRef;

  @ViewChild('divWithText')
  private divWithText!: ElementRef<HTMLElement>;

  tooltipSettings: ToolTipSettings = {
    show: false,
  };

  constructor(
    private delayService: DelayService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    // Every time we receive a show tooltip, we want to make sure that the mouse stays on the same position.
    // Otherwise, the tooltip will pop up accidentally, on casual mouse move.
    this.delayService.initSubscribes(DEFAULT_TOOLTIP_DELAY_TIME, this.showTooltip, this.hideTooltip);
  }

  activateTooltip(): void {
    this.delayService.activate();
  }

  deactivateTooltip(): void {
    this.delayService.deactivate();
  }

  /**
   * Checks if the text inside the element is truncated due to height constraints.
   *
   * @returns {boolean} `true` if the text is truncated, otherwise `false`
   */
  private isTextTruncated(): boolean {
    const element = this.divWithText.nativeElement;
    return (element.scrollHeight - element.clientHeight) > 1;
  }

  private showTooltip: () => void = () => {
    this.tooltipSettings.coordinates = this.divWithText.nativeElement.getBoundingClientRect();
    this.tooltipSettings.text = this.customTooltipText ?? this.text;
    this.tooltipSettings.show = this.isTooltipAlwaysShownOnHover || this.isTextTruncated();
    this.cdr.detectChanges();
  };

  private hideTooltip: () => void = () => {
    this.tooltipSettings.show = false;
  };
}
